import * as React from 'react';
import { ArrowRight, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ONBOARDING_STEPS,
  onboardingStepsFor,
  type OnboardingStep,
  type Role,
} from '@cfl/shared';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Progress,
} from '@/components/cargofl-ui';
import { useMyTeam, useOverview, type OverviewStats, type TeamRow } from '@/lib/analytics-api';
import {
  useOneOnOnes,
  useRecognitionFeed,
  type OneOnOne,
  type Recognition,
} from '@/lib/engagement-api';
import { useMyCurrentSelf, type SelfAssessmentSummary } from '@/lib/assessments-api';
import { useGoalSet, type GoalSet } from '@/lib/goals-api';
import { useKraSets, type KraSet } from '@/lib/kra-sets-api';
import { useCurrentUser } from '@/lib/session';
import { guideHref } from '@/lib/onboarding-guides';
import { cn } from '@/lib/utils';

/**
 * The dashboard's "Get started" checklist.
 *
 * Every tick is read from real data, never stored when somebody clicks the checklist,
 * so a step finished anywhere else in the app is finished here too, and nobody can
 * tick a box they have not done. Role-scoped like the sidebar: HR setup first, then
 * the team, then your own goals. It disappears once every step is done.
 */

export interface GetStartedStep {
  key: OnboardingStep;
  title: string;
  body: string;
  href: string;
  /** Undefined while the data behind it is still loading. */
  done: boolean | undefined;
}

export interface GetStartedInput {
  meId: string;
  role: Role;
  hasReports: boolean;
  goalSet: GoalSet | undefined;
  selfAssessment: SelfAssessmentSummary | undefined;
  team: TeamRow[] | undefined;
  meetings: OneOnOne[] | undefined;
  recognitionsGiven: Recognition[] | undefined;
  kraSets: KraSet[] | undefined;
  overview: OverviewStats | undefined;
}

const check = <T,>(data: T | undefined, test: (d: T) => boolean) =>
  data === undefined ? undefined : test(data);

/**
 * Which steps, and their titles, come from @cfl/shared - the worker's reminder reads
 * the same list. What is decided here is only whether each one is done.
 */
export function getStartedSteps(i: GetStartedInput): GetStartedStep[] {
  const goalsLive = i.goalSet !== undefined && i.goalSet.status !== 'DRAFT';
  // Steps this person does not get are never read, so their disabled queries do not matter.
  const done: Record<OnboardingStep, boolean | undefined> = {
    PEOPLE_SET_UP: check(i.overview, (o) => o.needsOnboarding === 0),
    KRA_GROUP: check(i.kraSets, (s) => s.length > 0),
    TEAM_GOALS: check(i.team, (t) => t.every((r) => r.goalsAccepted)),
    ONE_ON_ONE: check(i.meetings, (m) => m.some((x) => x.managerId === i.meId)),
    DEVELOPMENT_GOAL: check(i.goalSet, (s) => s.goals.some((g) => g.type === 'INDIVIDUAL')),
    LOG_PROGRESS: check(i.selfAssessment, (s) => s.exists && s.selfNotesLogged),
    // Matched on the giver, not the count: an API without the filter returns everyone's.
    RECOGNITION: check(i.recognitionsGiven, (r) => r.some((x) => x.fromEmployee.id === i.meId)),
  };
  const body: Record<OnboardingStep, string> = {
    PEOPLE_SET_UP:
      'Give everyone a department and a reporting manager. The reporting manager is who reviews them, so nobody without one can be reviewed.',
    KRA_GROUP:
      'Weight KRAs into a group totalling 100% and pin it to job titles. Everyone holding those titles gets their goals from it.',
    TEAM_GOALS:
      'Goals in draft are not live. Your reports cannot log progress or self-assess against them until you publish.',
    ONE_ON_ONE: 'Book time with one of your reports and keep the agenda and notes with the meeting.',
    DEVELOPMENT_GOAL:
      'A goal of your own choosing. It carries no weightage and never moves your rating; your reviewers read it at appraisal.',
    LOG_PROGRESS: goalsLive
      ? 'A short note on a KRA as the work happens, so your self-assessment is half-written when the window opens.'
      : 'Available once your manager publishes your goals.',
    RECOGNITION: 'Thank someone for work you saw. Open to everyone, and it never feeds anyone’s rating.',
  };
  return onboardingStepsFor(i).map((key) => ({
    key,
    ...ONBOARDING_STEPS[key],
    body: body[key],
    done: done[key],
  }));
}

/** Storage throws in some locked-down browsers; a checklist is never worth a white screen. */
function useStoredFlag(storage: () => Storage, key: string) {
  const [on, setOn] = React.useState(() => {
    try {
      return storage().getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const set = (next: boolean) => {
    setOn(next);
    try {
      if (next) storage().setItem(key, '1');
      else storage().removeItem(key);
    } catch {
      /* ignore */
    }
  };
  return [on, set] as const;
}

/**
 * This person's checklist, live. Shared by the card and the step-by-step guide, so the guide
 * celebrates on exactly the fact that ticks the card. Undefined `done` while loading.
 */
export function useGetStarted(): GetStartedStep[] {
  const me = useCurrentUser();
  const isHr = me.role === 'HR' || me.role === 'SUPER_ADMIN';

  // Same query keys the dashboard already uses, so most of these are cache hits.
  const { data: goalSet } = useGoalSet('me');
  const { data: selfAssessment } = useMyCurrentSelf();
  const { data: team } = useMyTeam(me.hasReports);
  const { data: meetings } = useOneOnOnes(me.hasReports);
  const { data: recognitionsGiven } = useRecognitionFeed(undefined, me.id);
  const { data: kraSets } = useKraSets(undefined, isHr);
  const { data: overview } = useOverview(isHr);

  return getStartedSteps({
    meId: me.id,
    role: me.role,
    hasReports: me.hasReports,
    goalSet,
    selfAssessment,
    team,
    meetings,
    recognitionsGiven,
    kraSets,
    overview,
  });
}

export function GetStartedCard() {
  const me = useCurrentUser();
  const [collapsed, setCollapsed] = useStoredFlag(() => localStorage, `cfl.getStarted.collapsed:${me.id}`);
  const steps = useGetStarted();
  const doneCount = steps.filter((s) => s.done).length;

  // Wait for every step rather than flash them all as undone; a failed query keeps it hidden.
  if (steps.some((s) => s.done === undefined) || doneCount === steps.length) return null;

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Get started</CardTitle>
            <CardDescription>
              Completed {doneCount}/{steps.length}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={collapsed ? 'Show steps' : 'Hide steps'}
              aria-expanded={!collapsed}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <ChevronDown /> : <ChevronUp />}
            </Button>
          </div>
        </div>
        <Progress
          value={(doneCount / steps.length) * 100}
          aria-label={`${doneCount} of ${steps.length} steps done`}
        />
      </CardHeader>

      {!collapsed && (
        <CardContent>
          <ol className="space-y-4">
            {steps.map((step, n) => (
              <li key={step.title} className="flex items-center gap-3">
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-full border text-sm',
                    step.done
                      ? 'border-success bg-success text-success-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {step.done ? <Check className="size-4" aria-hidden /> : n + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      step.done && 'text-muted-foreground line-through',
                    )}
                  >
                    {step.done && <span className="sr-only">Done: </span>}
                    {step.title}
                  </p>
                  <p className="text-sm text-muted-foreground">{step.body}</p>
                </div>
                {!step.done && (
                  <Button asChild variant="secondary" size="icon" aria-label={step.title}>
                    <Link to={guideHref(step.key, step.href)}>
                      <ArrowRight />
                    </Link>
                  </Button>
                )}
              </li>
            ))}
          </ol>
        </CardContent>
      )}
    </Card>
  );
}
